import { pxToEm } from 'util/pxToEm';
import { CampusLushLogo, Header, Footer, ButtonLink } from 'components/ui';
import { Box, BoxProps, Typography } from '@mui/material';
import React, { ReactElement, ReactNode } from 'react';
import { Image as ComponentImage } from 'components/lib';
import Image from 'next/image';
import Color from 'color';
import { Routes } from 'services/routes';

import backgroundImage from 'public/img/CampusLush - Background.png';
import { useRouter } from 'next/router';
import {
  SearchCircle,
  ThreeSmileyCircle,
  ExpandCircle,
} from 'components/icons';

export interface SplashFormLayoutProps extends BoxProps {
  children: ReactNode;
}

export const SplashFormLayout = ({
  children,
  ...boxProps
}: SplashFormLayoutProps): ReactElement => {
  const router = useRouter();
  const isLogin = router.pathname === '/login';

  const topHeader = (
    <Box
      bgcolor={{ xs: '#23305F', md: 'none' }}
      sx={{
        height: 60,
        p: 20,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <CampusLushLogo />
    </Box>
  );

  const infoSections = (
    image: ReactElement,
    primaryHeader: string,
    secondaryHeader: string,
  ) => (
    <Box
      my={{ xs: 16, sm: 16, md: 0 }}
      mb={{ md: 40, lg: 0 }}
      mr={{ lg: 32 }}
      ml={{ xs: 'unset', sm: 'unset', md: 'unset', lg: 'auto' }}
      maxWidth={{ xs: 'unset', md: 550 }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        fontSize: pxToEm(16),
      }}
    >
      <Box
        sx={{
          fontSize: pxToEm(100),
          lineHeight: 0,
          mb: 16,
          color: '#fff',
        }}
      >
        {image}
      </Box>
      <Typography fontWeight={500}>{primaryHeader}</Typography>
      <Typography>{secondaryHeader}</Typography>
    </Box>
  );

  const cards = (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
    >
      <Box
        mb={{ xs: 32, sm: 32, md: 0 }}
        mr={{ xs: 0, sm: 0, md: 24 }}
        sx={{
          p: 40,
          background: '#23305F',
          borderRadius: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        <Typography
          sx={{
            color: '#fff',
            mb: 24,
            fontSize: pxToEm(22),
            textAlign: 'center',
            fontWeight: 500,
          }}
        >
          We&apos;re excited for you to join.
        </Typography>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <ButtonLink
            href={Routes.signUp()}
            variant="vibrant"
            minWidth="290px"
            style={{ fontWeight: 400, fontSize: pxToEm(14) }}
          >
            <span> Create Account </span>
          </ButtonLink>
        </Box>
      </Box>
      <Box
        ml={{ xs: 'unset', sm: 'unset', md: 'auto' }}
        sx={{
          p: 40,
          background: 'rgba(35,48,95,0.1)',
          borderRadius: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        <Typography
          sx={{
            color: '#383838',
            mb: 24,
            textAlign: 'center',
            fontSize: pxToEm(22),
            fontWeight: 500,
          }}
        >
          Are you a school representative?
        </Typography>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <ButtonLink
            href="https://www.schools.campuslush.com"
            variant="tempered"
            minWidth="290px"
            style={{
              height: 40,
              border: '1px solid #23305F',
              background: '#fff',
              fontWeight: 400,
              fontSize: pxToEm(14),
            }}
          >
            <span> View CampusLush For Schools </span>
          </ButtonLink>
        </Box>
      </Box>
    </Box>
  );

  const infoBody = (
    <Box
      sx={{
        backgroundColor: '#f3f3f3',
      }}
    >
      <Box
        p={{ md: '120px 80px 0 80px', lg: '120px 120px' }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box display={{ xs: 'none', sm: 'none', md: 'block' }}>
          <ComponentImage
            src="/img/SchoolEvent.svg"
            alt="Event Image"
            width="100%"
            style={{
              maxWidth: 400,
              objectFit: 'contain',
              userSelect: 'none',
            }}
          />
        </Box>
        <Box p={24} width="100%">
          <Typography
            sx={{
              fontSize: pxToEm(30),
              fontWeight: 500,
            }}
          >
            A better way to get connected
          </Typography>
          <Typography
            maxWidth={{ xs: 320, md: 550, lg: 680 }}
            sx={{
              fontSize: pxToEm(18),
              mt: 16,
              mb: 32,
            }}
          >
            We’ve made it a lot easier for students and alumni to connect to
            more school events, resources, and communities.
          </Typography>
          <Box
            display={{ xs: 'flex', sm: 'flex', md: 'none' }}
            justifyContent={{ xs: 'center', sm: 'center', md: 'unset' }}
            p={20}
          >
            <ComponentImage
              src="/img/SchoolEvent.svg"
              alt="Event Image"
              width="100%"
              style={{
                maxWidth: 400,
                objectFit: 'contain',
                userSelect: 'none',
              }}
            />
          </Box>
          <Box
            display="flex"
            my={{ xs: 32, sm: 32, md: 0 }}
            mb={{ lg: 48 }}
            flexDirection={{ xs: 'column', md: 'column', lg: 'row' }}
          >
            {infoSections(
              <SearchCircle />,
              'Find Campus Events',
              'Explore campus events that interest you—from community service progams to parties, and everything in between.',
            )}
            {infoSections(
              <ThreeSmileyCircle />,
              'Discover School Organizations',
              'Get connected to your favorite organizations, whether you’re a future student, current student or alumni.',
            )}
            {infoSections(
              <ExpandCircle />,
              'Expand Your Connections',
              'Connect with old friends while making new ones at online or on campus events.',
            )}
          </Box>
          <Box display={{ md: 'none', lg: 'block' }}>{cards}</Box>
        </Box>
      </Box>
      <Box
        display={{ xs: 'none', md: 'block', lg: 'none' }}
        pb={{ md: 60 }}
        px={60}
      >
        {cards}
      </Box>
    </Box>
  );

  return (
    <Box width="100%" height="100%" {...boxProps}>
      {/* mobile */}
      <Box
        display={{ xs: 'flex', md: 'none' }}
        flexDirection="column"
        width="100%"
        height="100%"
      >
        {topHeader}
        <>
          <Box p="50px 30px" pb={200}>
            {isLogin && (
              <Typography
                sx={{
                  fontSize: pxToEm(26),
                  color: '#23305F',
                  fontWeight: 500,
                }}
              >
                The{' '}
                <Box
                  component="span"
                  sx={{
                    color: '#5cda64',
                    fontWeight: 500,
                    borderBottom: '3px solid yellow',
                  }}
                >
                  best
                </Box>{' '}
                way to discover events and organizations
                <Box
                  component="span"
                  sx={{
                    color: '#5cda64',
                  }}
                >
                  .
                </Box>
              </Typography>
            )}
            {children}
          </Box>
          {infoBody}
          <Footer variant="white" />
        </>
      </Box>

      {/* desktop */}
      <Box
        display={{ xs: 'none', md: 'flex' }}
        flexDirection="column"
        height="100%"
      >
        <Header loggedIn={false} showSearch={false}></Header>

        <Box flex={1} minHeight={0} position="relative">
          <Image
            src={backgroundImage}
            layout="fill"
            objectFit="cover"
            priority
          />

          <Box
            sx={{
              background: theme =>
                new Color(theme.palette.primary.main).alpha(0.7).string(),
              height: '100%',
              width: '100%',
              position: 'absolute',
            }}
          >
            <Box display="flex" height="100%">
              <Box
                flex={1}
                display="flex"
                flexDirection="column"
                height="100%"
                position="relative"
              >
                <Typography
                  sx={{
                    fontSize: pxToEm(49),
                    fontWeight: 500,
                    color: '#fff',
                    p: 80,
                    width: 655,
                    position: 'absolute',
                    top: 0,
                    right: 0,
                  }}
                >
                  The{' '}
                  <Box
                    component="span"
                    sx={{
                      color: '#5cda64',
                      fontWeight: 500,
                      borderBottom: '3px solid yellow',
                    }}
                  >
                    best
                  </Box>{' '}
                  way to discover events and organizations
                  <Box
                    component="span"
                    sx={{
                      color: '#5cda64',
                    }}
                  >
                    .
                  </Box>
                </Typography>
              </Box>

              <Box
                width="min(50%, 438px)"
                display="flex"
                alignItems="center"
                minHeight={0}
              >
                <Box
                  position="relative"
                  sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: '100%',
                    maxHeight: '100%',
                    background: '#fff',
                    overflowX: 'hidden',
                    overflowY: 'auto',
                  }}
                >
                  <Box
                    minHeight="100%"
                    position="relative"
                    p="80px 58px"
                    pb={200}
                  >
                    <Box>{children}</Box>
                  </Box>
                </Box>
              </Box>
            </Box>
            {infoBody}
            <Footer variant="white" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
